import React, { useState } from "react";
import BoardCardShell from "../BoardCardShell";
import BoardChartRegion from "../BoardChartRegion";
import BoardCardState from "../BoardCardState";
import RetentionVisualization from "features/analytics/studio/components/charts/RetentionVisualization";
import RetentionMetricTable from "features/analytics/studio/components/charts/RetentionMetricTable";
import {
  RETENTION_BREAKDOWN_CHART_TOP_DEFAULT,
  sliceBreakdownsForChart,
} from "features/analytics/studio/components/charts/BreakdownTopValuesControl";
import { formatNumber } from "shared/utils/numberFormatter";

// Badge uses feedback-success token — adapts to both light and dark themes
const BADGE = {
  label: "Retention",
  bg: "color-mix(in srgb, var(--feedback-success) 10%, transparent)",
  color: "var(--feedback-success)",
};

// Chart series palette — data colors intentionally stay fixed across themes
const PALETTE = [
  "#7c3aed", "var(--feedback-info)", "#10b981", "var(--feedback-warning)",
  "var(--feedback-error)", "#8b5cf6", "#06b6d4", "#84cc16",
];

export default function RetentionBoardCard({ card, result, onEdit, onDelete, readOnly }) {
  const [metricBreakdownTopN, setMetricBreakdownTopN] = useState(RETENTION_BREAKDOWN_CHART_TOP_DEFAULT);

  const intervals = result?.data?.intervals || [];
  const cohorts = result?.data?.cohorts || [];
  const weightedAverage = result?.data?.weightedAverage || null;
  const breakdowns = result?.data?.breakdowns || [];
  const metric = card?.queryDsl?.definition?.metric || "retention_rate";
  const config = card?.visualization?.config || {};
  const chartType = card?.visualization?.chartType || "retention-curve";
  const startEvent = card?.queryDsl?.definition?.startEvent || "Start Event";
  const returnEvent = card?.queryDsl?.definition?.returnEvent || "Return Event";
  const breakdownDef = card?.queryDsl?.definition?.breakdown;
  const breakdownDimensionLabel = String(breakdownDef?.propertyLabel || breakdownDef?.property || "").trim();

  const actions = readOnly ? [] : [
    ...(onEdit ? [{ label: "Edit", icon: "edit_pencil", onClick: onEdit }] : []),
    ...(onDelete ? [{ label: "Delete", icon: "delete", onClick: onDelete, danger: true }] : []),
  ];

  const state = !result
    ? "loading"
    : result.error
    ? "error"
    : intervals.length === 0 || cohorts.length === 0
    ? "empty"
    : null;
  const errorMsg = typeof result?.error === "string" ? result.error : "Could not load retention";

  if (!state && chartType === "metric") {
    return (
      <BoardCardShell
        title={card?.title || "Retention"}
        badge={BADGE}
        actions={actions}
        readOnly={readOnly}
      >
        <RetentionMetricBody
          intervals={intervals}
          weightedAverage={weightedAverage}
          breakdowns={breakdowns}
          metric={metric}
          startEvent={startEvent}
          returnEvent={returnEvent}
          config={config}
          breakdownDimensionLabel={breakdownDimensionLabel}
          metricBreakdownTopN={metricBreakdownTopN}
          setMetricBreakdownTopN={setMetricBreakdownTopN}
        />
      </BoardCardShell>
    );
  }

  return (
    <BoardCardShell
      title={card?.title || "Retention"}
      badge={BADGE}
      actions={actions}
      readOnly={readOnly}
    >
      {state ? (
        <BoardCardState state={state} message={state === "error" ? errorMsg : "No retention data"} />
      ) : (
        <BoardChartRegion>
          {({ height }) => (
            <RetentionVisualization
              intervals={intervals}
              cohorts={cohorts}
              weightedAverage={weightedAverage}
              breakdowns={breakdowns}
              breakdownDimensionLabel={breakdownDimensionLabel}
              metric={metric}
              config={config}
              chartType={chartType}
              startEventLabel={startEvent}
              returnEventLabel={returnEvent}
              hideCohortTable
              chartHeight={Math.max(80, height)}
              boardCompact
            />
          )}
        </BoardChartRegion>
      )}
    </BoardCardShell>
  );
}

function RetentionMetricBody({
  intervals, weightedAverage, breakdowns, metric,
  startEvent, returnEvent, config,
  breakdownDimensionLabel, metricBreakdownTopN, setMetricBreakdownTopN,
}) {
  const idx = typeof config?.retentionLineIntervalIndex === "number"
    ? config.retentionLineIntervalIndex : 0;
  const safeIdx = Array.isArray(intervals) && intervals.length > 0
    ? Math.min(Math.max(0, idx), intervals.length - 1) : 0;
  const groupLabel = intervals?.[safeIdx] || "<1 Day";
  const values = weightedAverage?.values || [];
  const selected = values[safeIdx] ?? values[0];
  const rawNum = selected?.value ?? selected;

  const formatVal = (raw) => {
    if (metric === "retention_rate") {
      const n = Number(raw);
      const pct = Number.isFinite(n) ? (n <= 1 ? n * 100 : n) : 0;
      return `${pct.toFixed(2)}%`;
    }
    const u = Number(raw);
    return Number.isFinite(u) ? formatNumber(u) : "0";
  };

  const hasBd = Array.isArray(breakdowns) && breakdowns.length > 0;
  const breakdownsForCards = hasBd ? sliceBreakdownsForChart(breakdowns, metricBreakdownTopN) : [];

  const rows = hasBd
    ? breakdowns.map((bd) => {
        const raw = Array.isArray(bd?.values) ? (bd.values[safeIdx] ?? bd.values[0]) : 0;
        const n = Number(raw);
        const sortValue = metric === "retention_rate"
          ? (Number.isFinite(n) ? (n <= 1 && n > 0 ? n * 100 : n) : 0)
          : (Number.isFinite(n) ? n : 0);
        return { label: bd?.segment || "(none)", value: formatVal(raw), sortValue };
      })
    : null;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-3">
      {hasBd ? (
        <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface mb-3">
          <div className="px-3 py-2 border-b border-[color:var(--border-default)]">
            <p className="text-[11px] text-[color:var(--text-muted)]">
              {`${startEvent} then ${returnEvent}`}{groupLabel ? ` • ${groupLabel}` : ""}
            </p>
          </div>
          <div className="p-3 grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))" }}>
            {breakdownsForCards.map((bd, i) => {
              const raw = Array.isArray(bd?.values) ? (bd.values[safeIdx] ?? bd.values[0]) : 0;
              const seg = bd?.segment || "(none)";
              const fullIdx = breakdowns.findIndex((b) => (b?.segment || "(none)") === seg);
              const color = PALETTE[(fullIdx >= 0 ? fullIdx : i) % PALETTE.length];
              return (
                <div key={`${seg}-${i}`} className="rounded-md p-2 border border-[color:var(--border-default)]">
                  <div className="text-[10px] mb-1 flex items-center gap-1.5 text-[color:var(--text-muted)]">
                    <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 2, backgroundColor: color, flexShrink: 0 }} />
                    <span className="truncate">{seg}</span>
                  </div>
                  <div className="font-bold truncate text-[color:var(--text-primary)]" style={{ fontSize: 18 }}>
                    {formatVal(raw)}
                  </div>
                  <div className="text-[10px] mt-0.5 text-[color:var(--text-muted)]">
                    {metric === "retention_rate" ? "Retention Rate" : "Unique Users"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className="rounded-md flex items-center justify-center mb-3 border border-[color:var(--border-default)]"
          style={{
            minHeight: 100,
            backgroundColor: "color-mix(in srgb, var(--brand-primary) 8%, transparent)",
          }}
        >
          <div className="text-center">
            <div className="text-[11px] mb-1 text-[color:var(--text-muted)]">
              {`${startEvent} then ${returnEvent}`}
            </div>
            <div
              className="font-bold text-[color:var(--brand-primary)]"
              style={{ fontSize: 32, lineHeight: 1.1 }}
            >
              {formatVal(rawNum)}
            </div>
            <div className="text-[11px] mt-1 text-[color:var(--text-muted)]">
              {metric === "retention_rate" ? "Retention Rate" : "Unique Users"} • {groupLabel}
            </div>
          </div>
        </div>
      )}
      <RetentionMetricTable
        label={`${startEvent} then ${returnEvent}`}
        value={formatVal(rawNum)}
        metricIsRetentionRate={metric === "retention_rate"}
        breakdownDimensionLabel={breakdownDimensionLabel}
        breakdownChartTopN={hasBd ? metricBreakdownTopN : undefined}
        onBreakdownChartTopNChange={hasBd ? setMetricBreakdownTopN : undefined}
        rows={rows}
      />
    </div>
  );
}
